<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { PlusSignCircleIcon, Search01Icon } from '@hugeicons/core-free-icons';
import HugeIcon from 'dashboard/components-next/icon/HugeIcon.vue';

const props = defineProps({
  items: {
    type: Array,
    required: true,
  },
  plusButtonClass: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['select', 'open', 'close']);

const isOpen = ref(false);
const searchQuery = ref('');
const showSearchInput = ref(false);
const triggerRef = ref(null);
const menuRef = ref(null);
const menuPosition = ref({ top: 0, left: 0 });

const filteredItems = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return props.items;

  return props.items.filter(
    item =>
      item.title.toLowerCase().includes(query) ||
      item.description.toLowerCase().includes(query)
  );
});

const close = () => {
  if (!isOpen.value) return;
  isOpen.value = false;
  searchQuery.value = '';
  showSearchInput.value = false;
  emit('close');
};

const updateMenuPosition = () => {
  const trigger = triggerRef.value;
  const menu = menuRef.value;
  if (!trigger) return;

  const triggerRect = trigger.getBoundingClientRect();
  const menuWidth = menu?.offsetWidth || 352;
  const menuHeight = menu?.offsetHeight || 420;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const centeredLeft = triggerRect.left + triggerRect.width / 2 - menuWidth / 2;
  const clampedLeft = Math.min(
    Math.max(16, centeredLeft),
    viewportWidth - menuWidth - 16
  );
  const opensAbove = triggerRect.bottom + menuHeight + 16 > viewportHeight;

  menuPosition.value = {
    top: opensAbove
      ? Math.max(16, triggerRect.top - menuHeight - 8)
      : triggerRect.bottom + 8,
    left: clampedLeft,
  };
};

const handleDocumentClick = event => {
  const trigger = triggerRef.value;
  const menu = menuRef.value;

  if (trigger?.contains(event.target) || menu?.contains(event.target)) {
    return;
  }

  close();
};

const toggle = () => {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    emit('open');
  } else {
    searchQuery.value = '';
    showSearchInput.value = false;
    emit('close');
  }
};

const toggleSearch = () => {
  showSearchInput.value = !showSearchInput.value;
  if (!showSearchInput.value) {
    searchQuery.value = '';
  }
};

const onSelect = actionKey => {
  emit('select', actionKey);
  close();
};

const onItemClick = item => {
  if (item.disabled) return;
  onSelect(item.key);
};

const PLUS_ICON_SIZE = 22;
const STEP_ICON_SIZE = 22;

watch(isOpen, async open => {
  if (open) {
    await nextTick();
    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    document.addEventListener('click', handleDocumentClick);
  } else {
    window.removeEventListener('resize', updateMenuPosition);
    window.removeEventListener('scroll', updateMenuPosition, true);
    document.removeEventListener('click', handleDocumentClick);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateMenuPosition);
  window.removeEventListener('scroll', updateMenuPosition, true);
  document.removeEventListener('click', handleDocumentClick);
});
</script>

<template>
  <div class="relative z-20 flex shrink-0 flex-col items-center">
    <button
      ref="triggerRef"
      type="button"
      class="relative z-20 flex size-[22px] items-center justify-center rounded-full border-0 bg-transparent p-0 shadow-none outline-none transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
      :class="plusButtonClass"
      :title="$t('AUTOMATION.ADD.ACTION_BUTTON_LABEL')"
      :aria-label="$t('AUTOMATION.ADD.ACTION_BUTTON_LABEL')"
      :aria-expanded="isOpen"
      :disabled="disabled"
      @click.stop="toggle"
    >
      <HugeIcon
        :icon="PlusSignCircleIcon"
        :size="PLUS_ICON_SIZE"
        class="pointer-events-none shrink-0"
      />
    </button>

    <Teleport to="body">
      <div
        v-if="isOpen"
        ref="menuRef"
        class="fixed z-[250] flex w-[22rem] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 shadow-lg dark:border-n-strong dark:bg-n-solid-2"
        :style="{
          top: `${menuPosition.top}px`,
          left: `${menuPosition.left}px`,
        }"
        role="menu"
        @click.stop
      >
        <div
          class="flex items-center justify-between gap-2 border-b border-n-weak px-3 py-2.5 dark:border-n-strong"
        >
          <span class="text-sm font-semibold text-n-slate-12">
            {{ $t('AUTOMATION.FLOW.ADD_STEPS.TITLE') }}
          </span>
          <button
            type="button"
            class="flex size-7 shrink-0 items-center justify-center rounded-md text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
            :aria-label="$t('AUTOMATION.FLOW.ADD_STEPS.SEARCH_PLACEHOLDER')"
            @click="toggleSearch"
          >
            <HugeIcon
              :icon="Search01Icon"
              :size="18"
              class="pointer-events-none shrink-0"
            />
          </button>
        </div>

        <div
          v-if="showSearchInput"
          class="border-b border-n-weak px-3 py-2 dark:border-n-strong"
        >
          <input
            v-model="searchQuery"
            type="search"
            class="h-9 w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand dark:border-n-strong dark:bg-n-solid-3"
            :placeholder="$t('AUTOMATION.FLOW.ADD_STEPS.SEARCH_PLACEHOLDER')"
          />
        </div>

        <ul class="m-0 max-h-80 list-none overflow-y-auto p-1">
          <li
            v-if="!filteredItems.length"
            class="px-3 py-4 text-center text-sm text-n-slate-10"
          >
            {{ $t('DROPDOWN_MENU.EMPTY_STATE') }}
          </li>
          <li v-for="item in filteredItems" :key="item.key" role="none">
            <button
              type="button"
              role="menuitem"
              :disabled="item.disabled"
              :title="item.disabled ? item.description : undefined"
              class="flex w-full gap-2.5 rounded-lg px-2 py-2.5 text-start transition-colors disabled:cursor-not-allowed disabled:opacity-50 hover:bg-n-alpha-2 disabled:hover:bg-transparent dark:hover:bg-n-alpha-3 dark:disabled:hover:bg-transparent"
              @click="onItemClick(item)"
            >
              <HugeIcon
                :icon="item.icon"
                :size="STEP_ICON_SIZE"
                class="pointer-events-none mt-0.5 shrink-0"
                :class="item.iconClass"
              />
              <span class="flex min-w-0 flex-1 flex-col gap-0.5">
                <span class="text-sm font-medium text-n-slate-12">{{
                  item.title
                }}</span>
                <span class="truncate text-xs leading-snug text-n-slate-10">
                  {{ item.description }}
                </span>
              </span>
            </button>
          </li>
        </ul>
      </div>
    </Teleport>
  </div>
</template>
